import { get, post, patch } from '../utils/api';
import type { NoticeToVacate } from '../containers/ResidentProfile/types';
import type { HouseholdTransferLeaseValidations } from '../utils/affordable';
import { getCookie } from '../utils/cookie';
import { addQueryData } from '../utils/query-helpers';

import request, { apiURL, requestToDownload, postDownload } from '../utils/api';
class LeaseService {
  saveNoticeToVacate(
    organizationId: string,
    propertyId: string,
    ntv: NoticeToVacate,
  ) {
    const options = {
      method: 'POST',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(ntv),
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/lease/${ntv.leaseId}/ntv`,
      options,
    );
  }
  deleteNoticeToVacate(
    organizationId: string,
    propertyId: string,
    ntv: NoticeToVacate,
  ) {
    const options = {
      method: 'DELETE',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/lease/${ntv.leaseId}/ntv`,
      options,
    );
  }
  generateLeaseDocument(lease: Object) {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return requestToDownload(`${apiURL}/lease/${lease.id}/generate`, options);
  }

  generateHUDLeaseDocument(
    affordableQualificationId: string,
    options?: Object,
  ) {
    const headers = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      ...options,
    };
    return requestToDownload(
      `${apiURL}/applications/${affordableQualificationId}/HUD/generate`,
      headers,
    );
  }

  generate811HUDLeaseDocument(
    affordableQualificationId: string,
    options?: Object = {},
  ) {
    const headers = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      ...options,
    };
    return requestToDownload(
      `${apiURL}/affordable-qualifications/${affordableQualificationId}/811HUD/generate`,
      headers,
    );
  }

  generateRDLeaseDocument(
    organizationId: string,
    propertyId: string,
    affordableQualificationId: string,
    variant: string | null,
    options?: Object,
  ) {
    // eslint-disable-next-line max-len
    const baseUrl = `${apiURL}/${organizationId}/${propertyId}/affordable-qualifications/${affordableQualificationId}/RD/generate`;
    const headers = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      ...options,
    };

    return requestToDownload(
      variant ? addQueryData(baseUrl, { variant }) : baseUrl,
      headers,
    );
  }

  updateFasReadiness(
    organizationId: string,
    propertyId: string,
    leaseId: string,
    fasReady: boolean,
  ) {
    const options = {
      method: 'PUT',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fasReady }),
    };
    return request(`${apiURL}/lease/${leaseId}/update`, options);
  }

  updateHasCommercialOptions(
    organizationId: string,
    propertyId: string,
    leaseId: string,
    hasCommercialOptions: boolean | null,
  ) {
    const options = {
      method: 'PUT',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ hasCommercialOptions }),
    };
    return request(`${apiURL}/lease/${leaseId}/update`, options);
  }

  updateReceivesHousingAssistance(
    organizationId: string,
    propertyId: string,
    leaseId: string,
    receivesHousingAssistance: boolean | null,
  ) {
    const options = {
      method: 'PUT',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ receivesHousingAssistance }),
    };
    return request(`${apiURL}/lease/${leaseId}/update`, options);
  }

  generateRenewalLeaseDocument(lease: Object) {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return requestToDownload(
      `${apiURL}/lease/${lease.id}/generateRenewal`,
      options,
    );
  }
  generateTransferLeaseDocument(lease: Object) {
    const options = {
      method: 'GET',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return requestToDownload(
      `${apiURL}/lease/${lease.id}/generateTransfer`,
      options,
    );
  }
  deleteMonthlyTransaction(
    organizationId: string,
    propertyId: string,
    leaseMonthlyTransactionId: string,
  ) {
    const options = {
      method: 'DELETE',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/lease-monthly-transaction/${leaseMonthlyTransactionId}`,
      options,
    );
  }

  getMonthlyLeaseTransactionsForResident(leaseId: string, options?: Object) {
    return get(`/lease/${leaseId}/leaseMonthlyTransactionResident`, options);
  }

  getMonthlyRequiredFeesForLease(leaseId: string, options?: Object) {
    return get(`/lease/${leaseId}/lease-monthly-required-fees`, options);
  }

  updateMonthlyRequiredFeesForLease(
    organizationId: string,
    propertyId: string,
    leaseId: string,
    requiredMonthlyFees: Array<Object>,
    removedMonthlyFees: Array<Object>,
  ) {
    const options = {
      method: 'PUT',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ requiredMonthlyFees, removedMonthlyFees }),
    };
    return request(
      `${apiURL}/${organizationId}/${propertyId}/lease/${leaseId}/lease-monthly-required-fees`,
      options,
    );
  }

  getLeaseHoldersSignatureStatus(
    organizationId: string,
    propertyId: string,
    leaseId: string,
    options?: Object,
  ) {
    return get(
      `/${organizationId}/${propertyId}/leases/${leaseId}/signatures/status`,
      options,
    );
  }

  cancelLease(
    organizationId: string,
    propertyId: string,
    leaseId: string,
    options?: Object,
  ) {
    return post(
      `/${organizationId}/${propertyId}/leases/${leaseId}/cancel`,
      null,
      options,
    );
  }

  sendToPortal(
    organizationId: string,
    propertyId: string,
    leaseId: string,
    options?: Object,
  ) {
    return post(
      `/${organizationId}/${propertyId}/leases/${leaseId}/send-to-portal`,
      null,
      options,
    );
  }

  executeLease(
    organizationId: string,
    propertyId: string,
    leaseId: string,
    signature: string,
    options?: Object,
  ) {
    const body = {
      sendToDocuments: true,
      showSignatures: true,
      landlord: {
        signature,
      },
    };
    return post(
      `/${organizationId}/${propertyId}/leases/${leaseId}/execute`,
      JSON.stringify(body),
      options,
    );
  }

  uploadLease(
    organizationId: string,
    propertyId: string,
    leaseId: string,
    document: Object,
    options?: Object,
  ) {
    /**
     * Have to make this requestOptions object to override the way that
     * currently POST is forcing Content-Type to be application/json.
     */
    const requestOptions = {
      ...options,
      headers: {
        Authorization: getCookie('session_id'),
      },
    };
    return post(
      `/${organizationId}/${propertyId}/leases/${leaseId}/upload`,
      document,
      requestOptions,
    );
  }

  generateCommercialLeaseAbstract(
    organizationId: string,
    propertyId: string,
    leaseId: string,
    fileName: string,
    options?: Object,
  ) {
    return postDownload(
      `/${organizationId}/${propertyId}/lease/${leaseId}/abstract?fileName=${fileName}`,
      fileName,
      '{}',
      options,
    );
  }

  getRenewalLeaseProrates(
    organizationId: string,
    propertyId: string,
    leaseId: string,
    options?: Object,
  ) {
    return get(
      `/${organizationId}/${propertyId}/lease/${leaseId}/renewal-prorates`,
      options,
    );
  }

  /**
   * ENG-30333 — REBA's opinion of the lease rent for a NEW lease (PricingType.New), keyed on the
   * unit, move-in date and lease term currently selected on the Lease Data tab — deliberately NOT
   * the lease's persisted values, since the point is to price whatever the user is about to save.
   * Sibling of `RenewalOfferService#getRenewalPricing` (ENG-30334, which keys off the property's
   * default term as of today instead): same underlying use case, different PricingType + an
   * explicit effectiveDate.
   *
   * Hits `lease-pricing` with these three query params, matching the route the backend actually
   * shipped (see Leases.js#getLeasePricingForLease). The response is NESTED, not flat:
   *   { hasPricing: boolean, price: { termId, termMonths, amount, isAuthoritative,
   *       provider: { id: string, name: string } | null } | null }
   * `amount`, `isAuthoritative` and `provider` all live on `price`, not on the top-level body —
   * a caller reading `data.amount`/`data.isAuthoritative`/`data.provider` directly will silently
   * get `undefined` forever. `provider` can be `null` even when `isAuthoritative` is true, so
   * don't assume the two travel together.
   *
   * Read-only and only ever a hint — the caller (`useRecommendedLeaseRent`) decides whether and
   * when to write this into the editable `leasedRent` field; this call never touches persisted
   * data. Expected to return `{ hasPricing: false, price: null }` — never an error — when
   * `reba-rev-mgmt` is off or no price applies to this unit/date/term combination.
   */
  getNewLeasePricing(
    organizationId: string,
    propertyId: string,
    leaseId: ?string,
    { unitId, moveInDate, leaseTermId }: Object,
    options?: Object,
  ) {
    // ENG-30333 — `leaseId` is optional because the Lease Data tab is reachable BEFORE the lease
    // is generated, and that pre-generation screen is where the rent PERSISTED at creation is
    // chosen. With no lease there is nothing to key the leaseId route on, so this falls back to
    // the unit-scoped route, which prices the NEW partition by construction (no lease exists, so
    // nothing could make it a renewal). Without this the pre-generation screen never consulted
    // the matrix at all and saved `Unit.quotingRentAmount` — a single scalar with no term
    // dimension, so every lease term persisted the same number.
    const url = leaseId
      ? addQueryData(
          `/${organizationId}/${propertyId}/lease/${leaseId}/lease-pricing`,
          { unitId, moveInDate, leaseTermId },
        )
      : addQueryData(
          `/${organizationId}/${propertyId}/unit/${unitId}/lease-pricing`,
          { moveInDate, leaseTermId },
        );
    return get(url, options);
  }

  validateHouseholdTransferLease(
    organizationId: string,
    propertyId: string,
    householdId: string,
  ): HouseholdTransferLeaseValidations {
    const options = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return get(
      `/${organizationId}/${propertyId}/household/${householdId}/validate-transfer-lease`,
      options,
    );
  }

  getLeaseRentSteps(leaseId: string) {
    return get(`/lease/${leaseId}/lease-rent/steps`);
  }

  saveLeaseRentSteps(
    leaseId,
    rentSteps,
    rentStepsToDelete,
    rentStepsToUpdate,
    rentStepsToCreate,
  ) {
    return patch(
      `/lease/${leaseId}/lease-rent/steps`,
      JSON.stringify({
        rentSteps,
        rentStepsToDelete,
        rentStepsToUpdate,
        rentStepsToCreate,
      }),
    );
  }
}

export default LeaseService;
